<template>
  <transition name="modal-fade" @enter="activeWrapper = true" @after-leave="onClosed">
    <div :class="classname" v-if="activeModal" ref="modal" @click="elementClick">
      <transition name="modal-scale" @enter="onModalEnter" @after-enter="afterModalEnter" @leave="onModalLeave">
        <div class="modal__wrapper" v-if="activeWrapper">
          <div ref="resizer" class="modal__resizer">
            <div class="modal__container" ref="container">
              <transition name="step">
                <span v-if="showCloseButton" class="modal__close" @click="close" />
              </transition>
              <template v-if="hasSteps">
                <transition
                  name="step"
                  @enter="onStepEnter"
                  @after-enter="afterStepEnter"
                  @leave="onStepLeave"
                  mode="out-in"
                  :duration="{ enter: 500, leave: 250 }"
                >
                  <div :key="currentStep" ref="step" class="modal__step">
                    <slot :name="currentStep" />
                  </div>
                </transition>
              </template>
              <div v-else-if="content" v-html="content" />
              <slot v-else />
            </div>
          </div>
        </div>
      </transition>
    </div>
  </transition>
</template>

<script>
import events from "@/bl-events/index"
import bemBlockModifiers from "@/utils/bem-block-modifiers"
import { cookie } from "@/utils/cookie"
import "polyfills/closest"

export default {
  name: "Modal",
  props: {
    id: { type: String, default: null },
    content: { type: String, default: null },
    hasCloseButton: { type: Boolean, default: true },
    closeOnBackgroundClick: { type: Boolean, default: true },
    trackingName: { type: String, default: "popup-20181026-modal_fallback" },
    timeout: { type: [String, Number], default: 0 },
    timeoutClose: { type: [String, Number], default: 0 },
    openActionName: { type: String, default: "1-open" },
    closeActionName: { type: String, default: "2-close" },
    show: { type: Boolean, default: false },
    modifiers: { type: Array, default: () => [] },
    cookie: { type: [String, Number], default: 0 },
    fixedSizes: { type: Boolean, default: false },
    closeOnEscape: { type: Boolean, default: true },
    step: { type: [Boolean, String], default: null }
  },
  data() {
    return {
      isBlocked: false,
      activeModal: false,
      activeWrapper: false,
      currentStep: null,
      inStepTransition: false,
      steps: this.$slots
    }
  },
  computed: {
    classname() {
      let modifiers = this.modifiers
      modifiers.push(this.id)
      return bemBlockModifiers("modal", this.modifiers)
    },
    hasSteps() {
      return this.stepKeys.length > 1
    },
    showCloseButton() {
      let showCloseButton = this.hasCloseButton
      if (this.hasSteps) {
        showCloseButton = showCloseButton && !this.inStepTransition
      }
      return showCloseButton
    },
    stepKeys() {
      return Object.keys(this.$slots)
    }
  },
  watch: {
    show(value) {
      if (value) {
        this.open()
      } else {
        this.close()
      }
    },
    step(value) {
      this.showStep(value)
    }
  },
  mounted() {
    if (cookie(`modal-${this.id}`)) {
      return
    }

    if (this.step) {
      this.showStep(this.step)
    }

    // Open modal on global event
    events.on(`modal:${this.id}:open`, (eventName, actionName) => this.open(eventName, actionName))

    // Close modal on global event
    events.on(`modal:${this.id}:close`, (eventName, actionName) => this.close(eventName, actionName))
    events.on("modal:all:close", this.close)

    // Show modal step on global event
    this.stepKeys.forEach((stepId) => {
      events.on(`modal:${this.id}:step:${stepId}:show`, () => {
        this.showStep(stepId)
      })
    })

    // Show modal next step on global event
    events.on(`modal:${this.id}:step:next`, this.nextStep)

    // Open modal on timeout
    if (this.timeout) {
      setTimeout(() => this.open(), parseInt(this.timeout))
    }

    events.on("modal:opened", (id) => {
      if (id !== this.id) {
        this.isBlocked = true
      }
    })

    events.on("modal:closed", () => {
      this.isBlocked = false
    })

    document.addEventListener("click", (e) => {
      if (e.target.closest(`[data-modal-open="${this.id}"]`)) {
        e.preventDefault()
        e.stopPropagation()
        this.open()
        return
      }

      if (e.target.closest(`[data-modal-close="${this.id}"]`)) {
        e.preventDefault()
        e.stopPropagation()
        this.close()
        return
      }

      this.stepKeys.forEach((stepId) => {
        if (e.target.closest(`[data-modal-open-step="${this.id}:${stepId}"]`)) {
          e.preventDefault()
          this.showStep(stepId)
        }
      })

      if (e.target.closest(`[data-modal-open-step-next="${this.id}"]`)) {
        e.preventDefault()
        e.stopPropagation()
        this.nextStep()
        return
      }
    })

    window.addEventListener("keydown", this.onKeyDown)

    if (this.show) this.open()
  },

  destroyed() {
    window.removeEventListener("keydown", this.onKeyDown)
  },

  methods: {
    open(eventName = this.trackingName, actionName = this.openActionName) {
      if (this.isBlocked) {
        return
      }
      this.activeModal = true
      if (this.hasSteps && !this.currentStep) {
        this.nextStep()
      } else if (this.timeoutClose && !this.hasSteps) {
        setTimeout(this.close, this.timeoutClose)
      }
      disableBodyScroll()
      events.track(eventName, actionName)
      this.$emit("open", eventName)
      if (this.cookie) {
        cookie(`modal-${this.id}`, 1, this.cookie)
      }
    },

    close(eventName = this.trackingName, actionName = this.closeActionName) {
      this.activeWrapper = false
      enableBodyScroll()
      events.track(eventName, actionName)
      this.$emit("close")
    },

    onClosed() {
      this.$emit("closed")
      events.emit(`modal:${this.id}:closed`)
    },

    showStep(id) {
      this.currentStep = id

      if (!id) {
        this.close()
        return
      }

      if (!this.activeModal) {
        this.open()
      }

      const isLastStep = this.stepKeys.indexOf(this.currentStep) >= this.stepKeys.length - 1
      if (isLastStep && this.timeoutClose) {
        setTimeout(this.close, this.timeoutClose)
      }
    },

    nextStep() {
      let nextStep
      if (!this.currentStep) {
        nextStep = this.stepKeys[0]
      } else {
        const index = this.stepKeys.indexOf(this.currentStep)
        if (index >= this.stepKeys.length) {
          this.close()
          return
        }
        nextStep = this.stepKeys[index + 1]
      }
      if (!nextStep) {
        return
      }
      this.showStep(nextStep)
    },

    onStepEnter() {
      this.inStepTransition = false
      events.emit(`modal:${this.id}:step:${this.currentStep}:opened`, this.$refs.el)

      /* Manage modal resize in between steps */
      // The complexity comes from the fact that CSS animation on width and height
      // doesn't work with 'auto' as origin or destination value

      // Save current modal dimensions
      const { clientWidth: resizerWidth, clientHeight: resizerHeight } = this.$refs.resizer
      // Set modal dimensions to auto, so they can adapt to the new content
      this.$refs.resizer.style.width = "auto"
      this.$refs.resizer.style.height = "auto"
      // Save modal dimensions with new content
      const { clientWidth: width, clientHeight: height } = this.$refs.step
      // Set back the current modal dimensions
      this.$refs.resizer.style.width = `${resizerWidth}px`
      this.$refs.resizer.style.height = `${resizerHeight}px`
      // Wait for next animation frame, else CSS animation doesn't trigger
      requestAnimationFrame(() => {
        // Set the dimensions of the modal with new content
        this.$refs.resizer.style.width = `${width}px`
        this.$refs.resizer.style.height = `${height}px`
      })
    },

    afterStepEnter() {
      // Set dimensions to auto so the modal can adapt to the content on window resize
      this.$refs.resizer.style.width = "auto"
      this.$refs.resizer.style.height = "auto"
      events.emit(`modal:${this.id}:step:${this.currentStep}:after-opened`)
    },

    onStepLeave() {
      this.inStepTransition = true
      // Hard set current modal dimension so we can keep them during the content change
      const { clientWidth: width, clientHeight: height } = this.$refs.resizer
      this.$refs.resizer.style.width = `${width}px`
      this.$refs.resizer.style.height = `${height}px`
      events.emit(`modal:${this.id}:step:${this.currentStep}:closed`)
    },

    onModalEnter() {
      events.emit("modal:opened", this.id)
      events.emit(`modal:${this.id}:opened`, this.$refs.modal)
      // Step event doesn't fire on modal open so we fire it manually
      if (this.hasSteps) {
        events.emit(`modal:${this.id}:step:${this.currentStep}:opened`)
      }
    },

    afterModalEnter() {
      events.emit(`modal:${this.id}:after-opened`)
      // Step event doesn't fire on modal open so we fire it manually
      if (this.hasSteps) {
        events.emit(`modal:${this.id}:step:${this.currentStep}:after-opened`)
      }
    },

    onModalLeave() {
      this.activeModal = false
      events.emit("modal:closed", this.id)
      events.emit(`modal:${this.id}:closed`)
    },

    elementClick(e) {
      if (!this.closeOnBackgroundClick) return
      let node = e.target
      while (node) {
        if (node === this.$refs.container) {
          return
        }
        node = node.parentNode
      }
      this.close()
    },

    onKeyDown(e) {
      if (e.code === "Escape") {
        if (this.closeOnEscape && this.activeModal) {
          this.close()
        }
      }
    }
  }
}

const body = document.querySelector("body")

const enableBodyScroll = () => {
  body.classList.remove("scroll-disabled")
}

const disableBodyScroll = () => {
  body.classList.add("scroll-disabled")
}
</script>

<style lang="scss">
.modal {
  $root: &;

  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 10000;

  display: flex;
  max-width: 100%;
  overflow-y: auto;

  -webkit-overflow-scrolling: touch;

  background: rgba($grey-dark, 0.7);

  &__wrapper {
    $margin-xs: map-get($container-paddings, xs);

    @include container-padding;

    display: flex;
    justify-content: center;

    align-items: center;
    max-width: 100%;
    margin: auto;
    padding-top: $margin-xs;
    padding-bottom: $margin-xs;

    @include media(min-width, s) {
      padding-top: $basex8;
      padding-bottom: $basex8;
    }
  }

  &__resizer {
    position: relative;

    display: flex;
    justify-content: center;
    align-items: center;
    max-width: 100%;
    margin: 0 auto;

    background-color: $white;

    border-radius: $spacing-4;

    transition: all 0.25s;
  }

  &__container {
    position: relative;
    z-index: 1;

    max-width: 100%;
  }

  &__close {
    position: absolute;
    top: 0;
    right: 0;
    z-index: 2;

    display: block;
    box-sizing: content-box;
    width: base(1.5);
    height: base(1.5);
    padding: $basex2;

    background-image: url("~@images/modal/close.svg");
    background-repeat: no-repeat;
    background-position: center center;
    cursor: pointer;

    &:hover {
      opacity: 0.5;
    }

    @include media(min-width, s) {
      top: $base;
      right: $base;
    }
  }

  &--wdyhau-survey {
    #{$root} {
      &__wrapper {
        @include container;
      }

      &__resizer {
        max-width: base(78);
      }
    }
  }

  &--active {
    display: flex;
  }

  &--dark {
    #{$root}__resizer {
      background-color: $blue-dark;
    }

    #{$root}__close {
      background-image: url("~@images/modal/close-dark.svg");
    }
  }

  &--grey-pale {
    #{$root}__resizer {
      background-color: $grey-pale;
    }
  }

  &--video {
    $video-background: black;

    #{$root} {
      &__wrapper,
      &__resizer {
        width: 100%;
        height: 100%;

        background-color: transparent;
      }

      &__container {
        background-color: $video-background;
      }

      &__close {
        top: 0;
        right: 0;
        z-index: 2;

        width: base(5.5);
        height: base(5.5);

        background-image: url("~@images/modal/close-video.svg");
      }
    }
  }

  &--auth-popup,
  &--no-horizontal-padding {
    #{$root} {
      &__wrapper {
        padding-right: 0;
        padding-left: 0;
      }
    }
  }

  &--no-padding {
    #{$root} {
      &__wrapper {
        padding: 0;
      }
    }
  }

  &--rounded-border {
    #{$root}__resizer {
      border-radius: $border-radius-4;
    }
  }
}

.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.25s;
}

.modal-fade-enter,
.modal-fade-leave-to {
  opacity: 0;
}

.modal-scale-enter-active,
.modal-scale-leave-active {
  transition: transform 0.25s;
}

.modal-scale-enter,
.modal-scale-leave-to {
  transform: scale(0.8);
}

.step-enter-active,
.step-leave-active {
  transition: opacity 0.25s;
}

.step-enter-active {
  transition-delay: 0.25s;
}

.step-enter,
.step-leave-to {
  opacity: 0;
}

body {
  &.scroll-disabled {
    height: 100%;
    overflow: hidden;
  }
}
</style>
